import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Spin, Switch, message } from 'antd';
import { PlusOutlined, DeleteOutlined, WifiOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, UsbOutlined, SyncOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ENV } from '@configs/env';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAuthStore } from '@stores/auth.store';
import { getEsp32Status, deleteEsp32FingerprintSlot, configureEsp32OverWifi, configureEsp32Wifi, configureEsp32ServerUrl } from '@services/attendance.service';
import {
    getGeneralSettings,
    patchGeneralSettings,
    getTenantSettings,
    putTenantSettings,
    patchTenantSettings,
} from '@services/settings.service';
import {
    RolesSectionTitle,
    RolesSectionSubtitle,
    ContentCard,
    ContentCardHeader,
    ContentCardTitle,
    ContentCardRow,
    ContentCardFooter,
    DetailLabel,
    MutedText,
    ErrorBlock,
    DetailPanelLoading,
} from './style';

const SettingsGeneralQueryKey = ['settings', 'general'];
const SettingsTenantQueryKey = ['settings', 'tenant'];

const arrayEqual = (a, b) => {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
};

const Settings = () => {
    const { pathname } = useLocation();
    const queryClient = useQueryClient();
    const role = useAuthStore((s) => s.role);
    const roles = useAuthStore((s) => s.roles ?? []);
    const isCompanyAdmin =
        role === 'Company Admin' || (Array.isArray(roles) && roles.includes('Company Admin'));

    const isOriginsPage = pathname.includes('/origins');

    const [local, setLocal] = useState({
        pushNotificationsEnabled: true,
        emailNotificationsEnabled: true,
        inAppNotificationsEnabled: true,
        origins: [],
    });
    const [newOrigin, setNewOrigin] = useState('');

    // IoT Biometric settings
    const [esp32Ip, setEsp32Ip] = useState(() => localStorage.getItem('code360_esp32_ip') || '192.168.1.15');
    const [statusLoading, setStatusLoading] = useState(false);
    const [deviceStatus, setDeviceStatus] = useState(null);
    const [deleteSlotId, setDeleteSlotId] = useState(1);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [mixedContentBlock, setMixedContentBlock] = useState(false);

    // OTA Wireless configuration state
    const [otaSsid, setOtaSsid] = useState('');
    const [otaPass, setOtaPass] = useState('');
    const [otaServerUrl, setOtaServerUrl] = useState(() => window.location.origin);
    const [otaLoading, setOtaLoading] = useState(false);
    const [wifiLoading, setWifiLoading] = useState(false);
    const [urlLoading, setUrlLoading] = useState(false);
    const [otaSuccess, setOtaSuccess] = useState(false);
    const [wifiSuccess, setWifiSuccess] = useState(false);
    const [urlSuccess, setUrlSuccess] = useState(false);
    const [otaCountdown, setOtaCountdown] = useState(0);
    const [otaUrlError, setOtaUrlError] = useState('');

    // Web Serial / USB Configuration Settings
    const [serialSupported, setSerialSupported] = useState(false);
    const [serialPort, setSerialPort] = useState(null);
    const [serialConnected, setSerialConnected] = useState(false);
    const [serialLogs, setSerialLogs] = useState([]);
    const [usbSsid, setUsbSsid] = useState('');
    const [usbPass, setUsbPass] = useState('');
    const [usbServerUrl, setUsbServerUrl] = useState(() => {
        const rawUrl = ENV.API_URL || '';
        return rawUrl.replace(/\/api\/v\d+\/?$/, '').replace(/\/$/, '');
    });
    const [sendingConfig, setSendingConfig] = useState(false);
    const [sendingWifi, setSendingWifi] = useState(false);
    const [sendingServer, setSendingServer] = useState(false);
    const [configTab, setConfigTab] = useState('wifi'); // 'wifi' or 'usb'
    const [customCommand, setCustomCommand] = useState('');
    const [autoScroll, setAutoScroll] = useState(true);
    
    const logsEndRef = useRef(null);

    useEffect(() => {
        if (typeof navigator !== 'undefined' && navigator.serial) {
            setSerialSupported(true);
        }
    }, []);


    useEffect(() => {
        if (autoScroll && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [serialLogs, autoScroll]);

    const connectSerial = async () => {
        try {
            const port = await navigator.serial.requestPort();
            await port.open({ baudRate: 115200 });
            setSerialPort(port);
            setSerialConnected(true);
            setSerialLogs(['--- USB Serial Port Opened (115200 Baud) ---', 'Type STATUS to verify connection']);
            message.success('USB connection established successfully!');
            
            // Start reading serial stream in background
            readSerialStream(port);
        } catch (err) {
            console.error('Serial connection failed:', err);
            message.error('Failed to open USB serial port. Check permissions or connection.');
        }
    };

    const disconnectSerial = async () => {
        if (serialPort) {
            try {
                await serialPort.close();
            } catch (err) {
                console.error(err);
            }
            setSerialPort(null);
            setSerialConnected(false);
            setSerialLogs((prev) => [...prev, '--- USB Serial Port Closed ---']);
            message.info('USB Serial Port disconnected.');
        }
    };

    const readSerialStream = async (port) => {
        try {
            while (port.readable) {
                const textDecoder = new TextDecoderStream();
                const readableStreamClosed = port.readable.pipeTo(textDecoder.writable);
                const reader = textDecoder.readable.getReader();
                try {
                    while (true) {
                        const { value, done } = await reader.read();
                        if (done) break;
                        if (value) {
                            setSerialLogs((prev) => {
                                const newLogs = [...prev, ...value.split('\n').map(l => l.trim()).filter(Boolean)];
                                if (newLogs.length > 200) return newLogs.slice(newLogs.length - 200);
                                return newLogs;
                            });
                        }
                    }
                } catch (err) {
                    console.error('Serial read loop error:', err);
                } finally {
                    reader.releaseLock();
                    await readableStreamClosed.catch(() => {});
                }
            }
        } catch (err) {
            console.error('Serial port reader failed:', err);
        }
    };

    const sendSerialCommand = async (command) => {
        if (!serialPort || !serialPort.writable) {
            message.error('USB Device is not connected or writable.');
            return false;
        }
        try {
            const encoder = new TextEncoder();
            const writer = serialPort.writable.getWriter();
            await writer.write(encoder.encode(command + '\r\n'));
            writer.releaseLock();
            setSerialLogs((prev) => [...prev, `[Sent]: ${command}`]);
            return true;
        } catch (err) {
            console.error('Serial write error:', err);
            message.error('Failed to send command over USB serial.');
            return false;
        }
    };

    const handleSendWifiConfig = async () => {
        if (!usbSsid) {
            message.warning('WiFi SSID Name is required');
            return;
        }
        setSendingWifi(true);
        try {
            setSerialLogs((prev) => [...prev, '>>> Syncing WiFi credentials to ESP32 board...']);
            
            // 1. Send SET_WIFI command
            const success = await sendSerialCommand(`SET_WIFI ${usbSsid}|${usbPass}`);
            if (success) {
                // Wait briefly, then send STATUS to show progress/reboot info
                await new Promise(r => setTimeout(r, 1000));
                await sendSerialCommand('STATUS');
                
                // Save to backend settings database
                patchMutation.mutate({
                    pushNotificationsEnabled: local.pushNotificationsEnabled,
                    emailNotificationsEnabled: local.emailNotificationsEnabled,
                    inAppNotificationsEnabled: local.inAppNotificationsEnabled,
                    origins: data?.origins ?? [],
                    wifiSsid: usbSsid,
                    wifiPass: usbPass,
                });
                message.success('WiFi credentials sent to device and saved to database!');
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to sync WiFi settings to device.');
        } finally {
            setSendingWifi(false);
        }
    };

    const handleSendServerConfig = async () => {
        if (!usbServerUrl) {
            message.warning('NestJS Backend URL is required');
            return;
        }
        // CRITICAL: Prevent localhost/127.0.0.1 loopback URLs which ESP32 cannot resolve
        const lowerUrl = usbServerUrl.toLowerCase();
        if (lowerUrl.includes('localhost') || lowerUrl.includes('127.0.0.1') || lowerUrl.includes('0.0.0.0')) {
            message.error('CRITICAL: ESP32 cannot connect to "localhost" or loopback IPs. Please enter your computer\'s actual local network IP (e.g., http://192.168.0.179:4009) or public VPS domain.');
            setSerialLogs((prev) => [...prev, '[ERROR]: Invalid Server URL. Loopback addresses like localhost/127.0.0.1 cannot be resolved by the ESP32.']);
            return;
        }

        setSendingServer(true);
        try {
            setSerialLogs((prev) => [...prev, '>>> Syncing NestJS Backend URL to ESP32 board...']);
            
            // 2. Send SET_SERVER command
            const success = await sendSerialCommand(`SET_SERVER ${usbServerUrl}`);
            if (success) {
                // Wait briefly, then send STATUS to verify
                await new Promise(r => setTimeout(r, 800));
                await sendSerialCommand('STATUS');
                
                // Save to backend settings database
                patchMutation.mutate({
                    pushNotificationsEnabled: local.pushNotificationsEnabled,
                    emailNotificationsEnabled: local.emailNotificationsEnabled,
                    inAppNotificationsEnabled: local.inAppNotificationsEnabled,
                    origins: data?.origins ?? [],
                    serverUrl: usbServerUrl,
                });
                message.success('NestJS Backend URL sent to device and saved to database!');
            }
        } catch (err) {
            console.error(err);
            message.error('Failed to sync Server URL to device.');
        } finally {
            setSendingServer(false);
        }
    };

    const handleTriggerPing = async () => {
        setSerialLogs((prev) => [...prev, '>>> Requesting device to ping backend...']);
        const success = await sendSerialCommand('PING');
        if (success) {
            message.success('Ping command sent over USB! Watch terminal logs for response.');
        }
    };

    const handleSendCustomCommand = async (e) => {
        e.preventDefault();
        const cmd = customCommand.trim();
        if (!cmd) return;
        await sendSerialCommand(cmd);
        setCustomCommand('');
    };

    const handleSaveEsp32Ip = () => {
        const cleanedIp = esp32Ip.trim();
        if (!cleanedIp) {
            message.warning('Please enter a valid IP address');
            return;
        }
        localStorage.setItem('code360_esp32_ip', cleanedIp);
        message.success('Device IP saved locally');
    };

    const handleTestConnection = async () => {
        setStatusLoading(true);
        setDeviceStatus(null);
        setMixedContentBlock(false);

        // IP is always kept fresh by the 10-second poll above — just use it directly
        const activeIp = esp32Ip.trim();

        if (!activeIp) {
            message.warning('No device IP available. Please wait for the ESP32 to connect and ping the backend.');
            setStatusLoading(false);
            return;
        }

        try {
            const data = await getEsp32Status(activeIp);
            setDeviceStatus({
                success: true,
                wifi: data.wifi_connected || 'Connected',
                ssid: data.ssid || 'Unknown',
                sensor: data.fingerprint_sensor_found !== false ? 'Found & Active' : 'Not Detected',
                sensorOk: data.fingerprint_sensor_found !== false,
                baseUrl: data.base_url || '',
            });
            if (data.base_url && data.base_url.length > 0) {
                setOtaServerUrl(data.base_url);
            }
            message.success('Successfully connected to biometric device!');
        } catch (err) {
            console.error('ESP32 Status fetch failed:', err);
            setMixedContentBlock(true);
            setDeviceStatus({
                success: false,
                message: err.message || 'Connection timed out or blocked',
            });
            message.error('Failed to connect to device.');
        } finally {
            setStatusLoading(false);
        }
    };

    const handleDeleteSlot = async () => {
        if (!deleteSlotId || deleteSlotId < 1 || deleteSlotId > 162) {
            message.warning('Slot ID must be between 1 and 162.');
            return;
        }
        setDeleteLoading(true);
        const cleanedIp = esp32Ip.trim();
        localStorage.setItem('code360_esp32_ip', cleanedIp);

        try {
            await deleteEsp32FingerprintSlot(cleanedIp, deleteSlotId);
            message.success(`Fingerprint template in slot ${deleteSlotId} deleted successfully from device!`);
        } catch (err) {
            console.error('Delete fingerprint slot failed:', err);
            message.error(err.message || 'Failed to delete fingerprint slot from device.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const validateOtaUrl = (url) => {
        const trimmed = url.trim().toLowerCase();
        if (trimmed.includes('localhost') || trimmed.includes('127.0.0.1')) {
            return '⚠️ Do NOT use localhost or 127.0.0.1 — the ESP32 cannot reach your computer via loopback. Use your LAN IP (e.g. http://192.168.1.100:4009) or VPS domain.';
        }
        return '';
    };

    const handleOtaUrlChange = (e) => {
        const val = e.target.value;
        setOtaServerUrl(val);
        setOtaUrlError(validateOtaUrl(val));
    };

    const handleOtaSync = async () => {
        const urlErr = validateOtaUrl(otaServerUrl);
        if (urlErr) {
            setOtaUrlError(urlErr);
            return;
        }
        if (!otaSsid && !otaServerUrl) {
            message.warning('Please fill in at least WiFi SSID or the Server URL.');
            return;
        }
        setOtaLoading(true);
        setOtaSuccess(false);
        setOtaCountdown(0);
        try {
            const cleanedIp = esp32Ip.trim();
            await configureEsp32OverWifi(cleanedIp, {
                ssid: otaSsid,
                pass: otaPass,
                url: otaServerUrl,
            });
            setOtaSuccess(true);
            let count = 12;
            setOtaCountdown(count);
            const interval = setInterval(() => {
                count -= 1;
                setOtaCountdown(count);
                if (count <= 0) {
                    clearInterval(interval);
                    setOtaCountdown(0);
                }
            }, 1000);
            message.success('Configuration sent! Device is rebooting...');
        } catch (err) {
            console.error('OTA configure failed:', err);
            if (err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK') {
                message.error('Cannot reach device. Ensure the ESP32 IP is correct and device is on the local network.');
            } else {
                message.error(err?.response?.data?.error || 'Failed to configure device wirelessly.');
            }
        } finally {
            setOtaLoading(false);
        }
    };

    // Save WiFi credentials only — device will reboot to connect to new network
    const handleSaveWifi = async () => {
        if (!otaSsid.trim()) {
            message.warning('Please enter a WiFi SSID.');
            return;
        }
        setWifiLoading(true);
        setWifiSuccess(false);
        setOtaCountdown(0);
        try {
            const cleanedIp = esp32Ip.trim();
            await configureEsp32Wifi(cleanedIp, { ssid: otaSsid, pass: otaPass });
            setWifiSuccess(true);
            // Countdown for reboot
            let count = 12;
            setOtaCountdown(count);
            const interval = setInterval(() => {
                count -= 1;
                setOtaCountdown(count);
                if (count <= 0) { clearInterval(interval); setOtaCountdown(0); }
            }, 1000);
            message.success('WiFi credentials saved! Device is rebooting...');
        } catch (err) {
            console.error('WiFi configure failed:', err);
            if (err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK') {
                message.error('Cannot reach device. Check the ESP32 IP and local network.');
            } else {
                message.error(err?.response?.data?.error || 'Failed to save WiFi credentials.');
            }
        } finally {
            setWifiLoading(false);
        }
    };

    // Save backend server URL only — no reboot needed
    const handleSaveServerUrl = async () => {
        const urlErr = validateOtaUrl(otaServerUrl);
        if (urlErr) {
            setOtaUrlError(urlErr);
            return;
        }
        if (!otaServerUrl.trim()) {
            message.warning('Please enter the backend server URL.');
            return;
        }
        setUrlLoading(true);
        setUrlSuccess(false);
        try {
            const cleanedIp = esp32Ip.trim();
            await configureEsp32ServerUrl(cleanedIp, otaServerUrl);
            setUrlSuccess(true);
            message.success('Server URL saved to device! No reboot required.');
        } catch (err) {
            console.error('Server URL configure failed:', err);
            if (err?.message?.includes('Network Error') || err?.code === 'ERR_NETWORK') {
                message.error('Cannot reach device. Check the ESP32 IP and local network.');
            } else {
                message.error(err?.response?.data?.error || 'Failed to save server URL.');
            }
        } finally {
            setUrlLoading(false);
        }
    };

    const queryKey = isCompanyAdmin ? SettingsTenantQueryKey : SettingsGeneralQueryKey;
    const queryFn = isCompanyAdmin ? getTenantSettings : getGeneralSettings;

    const { data, isLoading, isError, refetch } = useQuery({
        queryKey,
        queryFn,
        enabled: true,
        staleTime: 0,
        refetchOnMount: true,
    });

    useEffect(() => {
        refetch();
    }, [pathname, refetch]);

    useEffect(() => {
        if (data) {
            if (data.wifiSsid) {
                setUsbSsid(data.wifiSsid);
                setOtaSsid(data.wifiSsid);
            }
            if (data.wifiPass) {
                setUsbPass(data.wifiPass);
                setOtaPass(data.wifiPass);
            }
            const backendServerUrl = data.serverUrl;
            if (backendServerUrl && backendServerUrl.length > 0 && !backendServerUrl.includes('127.0.0.1') && !backendServerUrl.includes('0.0.0.0') && !backendServerUrl.includes('localhost')) {
                setUsbServerUrl(backendServerUrl);
                setOtaServerUrl(backendServerUrl);
            }
            const backendDeviceIp = data.deviceIp;
            if (backendDeviceIp && backendDeviceIp.length > 0 && backendDeviceIp !== '127.0.0.1' && backendDeviceIp !== '0.0.0.0') {
                setEsp32Ip((prev) => {
                    if (prev !== backendDeviceIp) {
                        localStorage.setItem('code360_esp32_ip', backendDeviceIp);
                        return backendDeviceIp;
                    }
                    return prev;
                });
            }
        }
    }, [data]);

    const patchMutation = useMutation({
        mutationFn: isCompanyAdmin ? putTenantSettings : patchGeneralSettings,
        onSuccess: (updated) => {
            queryClient.setQueryData(queryKey, updated);
            message.success('Settings saved');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to save settings');
        },
    });

    const patchOriginsMutation = useMutation({
        mutationFn: (body) => patchTenantSettings(body),
        onSuccess: (updated) => {
            queryClient.setQueryData(SettingsTenantQueryKey, (prev) =>
                prev ? { ...prev, origins: updated?.origins ?? prev.origins } : updated,
            );
            message.success('Origins saved');
        },
        onError: (err) => {
            message.error(err?.response?.data?.message || 'Failed to save origins');
        },
    });

    useEffect(() => {
        if (data) {
            setLocal((prev) => ({
                ...prev,
                pushNotificationsEnabled: data.pushNotificationsEnabled ?? true,
                emailNotificationsEnabled: data.emailNotificationsEnabled ?? true,
                inAppNotificationsEnabled: data.inAppNotificationsEnabled ?? true,
                origins: Array.isArray(data.origins) ? [...data.origins] : [],
            }));
        }
    }, [data]);

    // ─── Sync device IP from getTenantSettings response ─────────────────────
    // deviceIp is injected into the settings response from the ESP32's ping.
    useEffect(() => {
        const backendIp = data?.deviceIp;
        if (backendIp && backendIp.length > 0 && backendIp !== '127.0.0.1' && backendIp !== '0.0.0.0') {
            setEsp32Ip((prev) => {
                if (prev !== backendIp) {
                    localStorage.setItem('code360_esp32_ip', backendIp);
                    return backendIp;
                }
                return prev;
            });
        }
    }, [data?.deviceIp]);

    const handleSaveGeneral = () => {
        if (isCompanyAdmin) {
            patchMutation.mutate({
                pushNotificationsEnabled: local.pushNotificationsEnabled,
                emailNotificationsEnabled: local.emailNotificationsEnabled,
                inAppNotificationsEnabled: local.inAppNotificationsEnabled,
                origins: data?.origins ?? [],
            });
        } else {
            patchMutation.mutate({
                pushNotificationsEnabled: local.pushNotificationsEnabled,
                emailNotificationsEnabled: local.emailNotificationsEnabled,
                inAppNotificationsEnabled: local.inAppNotificationsEnabled,
            });
        }
    };

    const handleSaveOrigins = () => {
        patchOriginsMutation.mutate({ origins: local.origins });
    };

    const dataOrigins = useMemo(
        () => (Array.isArray(data?.origins) ? data.origins : []),
        [data?.origins],
    );
    const notificationChanged =
        data &&
        (local.pushNotificationsEnabled !== data.pushNotificationsEnabled ||
            local.emailNotificationsEnabled !== data.emailNotificationsEnabled ||
            local.inAppNotificationsEnabled !== data.inAppNotificationsEnabled);
    const originsChanged = data && !arrayEqual(local.origins, dataOrigins);
    const hasChangesGeneral = !!notificationChanged;
    const hasChangesOrigins = !!originsChanged;

    const addOrigin = () => {
        const val = newOrigin?.trim();
        if (!val) {
            message.warning('Please type in the URL first.');
            return;
        }
        if (local.origins.includes(val)) {
            message.warning('This origin is already in the list');
            return;
        }
        setLocal((s) => ({ ...s, origins: [...s.origins, val] }));
        setNewOrigin('');
    };

    const removeOrigin = (index) => {
        setLocal((s) => ({
            ...s,
            origins: s.origins.filter((_, i) => i !== index),
        }));
    };

    const showGeneral = !isOriginsPage;
    const showOrigins = isOriginsPage;
    const needDataForView =
        showGeneral || (showOrigins && isCompanyAdmin);
    const isLoadingView = needDataForView && isLoading;

    if (isLoadingView) {
        return (
            <Container
                $display="flex"
                $flexDirection="column"
                $gap="32px"
                $width="100%"
                $maxWidth="720px"
                $margin="0 auto"
            >
                <DetailPanelLoading>
                    <Spin size="large" />
                </DetailPanelLoading>
            </Container>
        );
    }

    return (
        <Container $display="flex" $flexDirection="column" $gap="32px" $width="100%" $maxWidth="720px" $margin="0 auto">
            <Container $marginBottom="8px">
                <RolesSectionTitle>Settings</RolesSectionTitle>
                <RolesSectionSubtitle>
                    {showGeneral
                        ? 'Manage your account and preferences'
                        : 'Manage allowed origins for lead capture'}
                </RolesSectionSubtitle>
            </Container>

            {isError && showGeneral && (
                <ErrorBlock>
                    Unable to load settings. Check that the API (GET
                    /api/v1/settings/{isCompanyAdmin ? 'tenant' : 'general'}) is available.
                </ErrorBlock>
            )}
            {isError && showOrigins && isCompanyAdmin && (
                <ErrorBlock>
                    Unable to load origins. Check that the API (GET /api/v1/settings/tenant) is available.
                </ErrorBlock>
            )}

            {showGeneral && (
                <>
                    <ContentCard $marginBottom="0" $overflow="hidden">
                        <ContentCardHeader>
                            <ContentCardTitle>{isCompanyAdmin ? 'Tenant settings' : 'General'}</ContentCardTitle>
                        </ContentCardHeader>
                        <ContentCardRow>
                            <Container $flex="1" $minWidth="0" $display="flex" $flexDirection="column" $gap="2px">
                                <DetailLabel as="div">Push notifications</DetailLabel>
                                <MutedText>Receive push notifications in your browser</MutedText>
                            </Container>
                            <Switch
                                checked={local.pushNotificationsEnabled}
                                onChange={(checked) => setLocal((s) => ({ ...s, pushNotificationsEnabled: checked }))}
                            />
                        </ContentCardRow>
                        <ContentCardRow>
                            <Container $flex="1" $minWidth="0" $display="flex" $flexDirection="column" $gap="2px">
                                <DetailLabel as="div">Email notifications</DetailLabel>
                                <MutedText>Receive updates and alerts by email</MutedText>
                            </Container>
                            <Switch
                                checked={local.emailNotificationsEnabled}
                                onChange={(checked) => setLocal((s) => ({ ...s, emailNotificationsEnabled: checked }))}
                            />
                        </ContentCardRow>
                        <ContentCardRow>
                            <Container $flex="1" $minWidth="0" $display="flex" $flexDirection="column" $gap="2px">
                                <DetailLabel as="div">In-app notifications</DetailLabel>
                                <MutedText>Show notifications in the app header</MutedText>
                            </Container>
                            <Switch
                                checked={local.inAppNotificationsEnabled}
                                onChange={(checked) => setLocal((s) => ({ ...s, inAppNotificationsEnabled: checked }))}
                            />
                        </ContentCardRow>
                        <ContentCardFooter>
                            <Button
                                $primary
                                $blackOnLight
                                $size="14px"
                                onClick={handleSaveGeneral}
                                loading={patchMutation.isPending}
                                disabled={!hasChangesGeneral}
                            >
                                Save changes
                            </Button>
                        </ContentCardFooter>
                    </ContentCard>

                    <ContentCard $marginTop="24px" $overflow="hidden" style={{ marginTop: '24px' }}>
                        <ContentCardHeader>
                            <ContentCardTitle>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <WifiOutlined style={{ color: '#4f46e5' }} />
                                    IoT & Biometric Device Manager
                                </span>
                            </ContentCardTitle>
                        </ContentCardHeader>

                        {/* Interactive Tab Selector */}
                        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <button
                                type="button"
                                onClick={() => setConfigTab('wifi')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    border: 'none',
                                    background: 'transparent',
                                    borderBottom: configTab === 'wifi' ? '3px solid #4f46e5' : 'none',
                                    color: configTab === 'wifi' ? '#4f46e5' : '#6b7280',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                <WifiOutlined />
                                Configure via Local WiFi
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfigTab('usb')}
                                style={{
                                    flex: 1,
                                    padding: '12px',
                                    border: 'none',
                                    background: 'transparent',
                                    borderBottom: configTab === 'usb' ? '3px solid #4f46e5' : 'none',
                                    color: configTab === 'usb' ? '#4f46e5' : '#6b7280',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                <UsbOutlined />
                                Provision via USB (Web Serial)
                            </button>
                        </div>
                        
                        {configTab === 'wifi' ? (
                            <>
                                <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                        <DetailLabel style={{ fontWeight: 600 }}>ESP32 Local IP Configuration</DetailLabel>
                                        <MutedText>Configure the IP address of the physical biometric scanner on your local office network.</MutedText>
                                        <Container $display="flex" $gap="12px" $alignItems="center" $flexWrap="wrap" $marginTop="4px">
                                            <Input
                                                placeholder="e.g. 192.168.1.15"
                                                value={esp32Ip}
                                                onChange={(e) => setEsp32Ip(e.target.value)}
                                                style={{ maxWidth: 320, flex: 1, minWidth: 200 }}
                                            />
                                            <Button
                                                $primary
                                                $blackOnLight
                                                $size="14px"
                                                onClick={handleSaveEsp32Ip}
                                                $padding="10px 16px"
                                            >
                                                Save IP
                                            </Button>
                                            <Button
                                                $primary={false}
                                                $size="14px"
                                                onClick={handleTestConnection}
                                                disabled={statusLoading}
                                                $padding="10px 16px"
                                                style={{
                                                    border: '1px solid #4f46e5',
                                                    color: '#4f46e5',
                                                    background: 'transparent',
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                             {statusLoading ? <LoadingOutlined /> : <WifiOutlined />}
                                                            Test Connection
                                                            </Button>
                                                            <Button
                                                                $primary={false}
                                                                $size="14px"
                                                                onClick={() => {
                                                                    refetch().then(({ data: freshData }) => {
                                                                        const ip = freshData?.deviceIp;
                                                                        if (ip && ip.length > 0 && ip !== '127.0.0.1') {
                                                                            setEsp32Ip(ip);
                                                                            localStorage.setItem('code360_esp32_ip', ip);
                                                                            message.success(`IP synced from backend: ${ip}`);
                                                                        } else {
                                                                            message.warning('No device IP found. Make sure the ESP32 is connected to WiFi and has pinged the backend.');
                                                                        }
                                                                    });
                                                                }}
                                                                $padding="10px 16px"
                                                                style={{
                                                                    border: '1px solid #059669',
                                                                    color: '#059669',
                                                                    background: 'transparent',
                                                                    fontWeight: 600,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <SyncOutlined /> Sync IP
                                                            </Button>
                                                        </Container>
                                                    </Container>
                                                </ContentCardRow>

                                {deviceStatus && (
                                    <ContentCardRow style={{ background: '#f9fafb', flexDirection: 'column', alignItems: 'stretch', padding: '16px 20px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            <span style={{ fontWeight: 600, fontSize: '13px', color: '#374151' }}>Device Diagnostics:</span>
                                            {deviceStatus.success ? (
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginTop: '4px' }}>
                                                    <div style={{ background: 'white', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Wifi Status</div>
                                                        <div style={{ fontWeight: 600, color: '#10b981', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13.5px', marginTop: '2px' }}>
                                                            <CheckCircleOutlined />
                                                            Connected
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'white', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Wi-Fi SSID</div>
                                                        <div style={{ fontWeight: 600, color: '#374151', fontSize: '13.5px', marginTop: '2px' }}>
                                                            "{deviceStatus.ssid}"
                                                        </div>
                                                    </div>
                                                    <div style={{ background: 'white', padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                        <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' }}>Optical Sensor</div>
                                                        <div style={{ 
                                                            fontWeight: 600, 
                                                            color: deviceStatus.sensorOk ? '#10b981' : '#ef4444', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            gap: '6px', 
                                                            fontSize: '13.5px', 
                                                            marginTop: '2px' 
                                                        }}>
                                                            {deviceStatus.sensorOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                                            {deviceStatus.sensor}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div style={{ color: '#ef4444', fontWeight: 500, fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <CloseCircleOutlined />
                                                    Device Offline or Blocked ({deviceStatus.message})
                                                </div>
                                            )}
                                        </div>
                                    </ContentCardRow>
                                )}

                                {mixedContentBlock && (
                                    <ContentCardRow style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', padding: '14px 20px', flexDirection: 'column', alignItems: 'stretch' }}>
                                        <div style={{ color: '#b45309', fontWeight: 600, fontSize: '13px' }}>Mixed Content Warning:</div>
                                        <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px', lineHeight: '1.5' }}>
                                            Modern browsers running over HTTPS block direct unencrypted HTTP connections to devices on the local LAN.
                                            To resolve this, ensure you are running the dashboard over HTTP or configure your browser to <strong>allow insecure content</strong> (mixed content) for local IP <strong>{esp32Ip}</strong>.
                                        </div>
                                    </ContentCardRow>
                                )}

                                {/* OTA Wireless Configuration - WiFi Section */}
                                <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                        <DetailLabel style={{ fontWeight: 700, fontSize: '14px' }}>
                                            <WifiOutlined style={{ color: '#4f46e5', marginRight: 6 }} />
                                            Configure Device WiFi Credentials
                                        </DetailLabel>
                                        <MutedText>
                                            Change the WiFi network the device connects to. The device will reboot automatically to apply and connect to the new network.
                                        </MutedText>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginTop: '8px' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>WiFi SSID</label>
                                                <Input
                                                    placeholder="e.g. Office_WiFi"
                                                    value={otaSsid}
                                                    onChange={(e) => setOtaSsid(e.target.value)}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>WiFi Password</label>
                                                <Input
                                                    isPassword
                                                    placeholder="WiFi Password"
                                                    value={otaPass}
                                                    onChange={(e) => setOtaPass(e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        {/* Success / Reboot Countdown Banner */}
                                        {wifiSuccess && otaCountdown > 0 && (
                                            <div style={{
                                                background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                                                border: '1px solid #6ee7b7',
                                                borderRadius: '10px',
                                                padding: '14px 18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                marginTop: '4px'
                                            }}>
                                                <LoadingOutlined style={{ color: '#059669', fontSize: '18px' }} />
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#065f46', fontSize: '13.5px' }}>WiFi configuration saved! Device is rebooting...</div>
                                                    <div style={{ color: '#047857', fontSize: '12px', marginTop: '2px' }}>
                                                        Please wait <strong>{otaCountdown}s</strong> while the device reconnects to the new network.
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {wifiSuccess && otaCountdown === 0 && (
                                            <div style={{
                                                background: '#f0fdf4',
                                                border: '1px solid #86efac',
                                                borderRadius: '10px',
                                                padding: '12px 18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginTop: '4px'
                                            }}>
                                                <CheckCircleOutlined style={{ color: '#16a34a', fontSize: '16px' }} />
                                                <div style={{ color: '#15803d', fontWeight: 600, fontSize: '13px' }}>
                                                    WiFi reconnect window completed. Click &quot;Test Connection&quot; to confirm device is reachable.
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ marginTop: '8px' }}>
                                            <Button
                                                $primary
                                                $blackOnLight
                                                $size="14px"
                                                onClick={handleSaveWifi}
                                                disabled={wifiLoading}
                                                $padding="10px 20px"
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                {wifiLoading ? <LoadingOutlined /> : <WifiOutlined />}
                                                Save WiFi Credentials
                                            </Button>
                                        </div>
                                    </Container>
                                </ContentCardRow>

                                {/* OTA Wireless Configuration - Server URL Section */}
                                <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                        <DetailLabel style={{ fontWeight: 700, fontSize: '14px' }}>
                                            <SyncOutlined style={{ color: '#4f46e5', marginRight: 6 }} />
                                            Configure Backend Server URL
                                        </DetailLabel>
                                        <MutedText>
                                            Update the server API endpoint that the device pings and reports scan events to. This does not require rebooting the device.
                                        </MutedText>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>Backend Server URL</label>
                                            <Input
                                                placeholder="e.g. https://api.yourdomain.com or http://192.168.0.100:4009"
                                                value={otaServerUrl}
                                                onChange={handleOtaUrlChange}
                                                status={otaUrlError ? 'error' : ''}
                                            />
                                            {otaUrlError ? (
                                                <span style={{ fontSize: '11px', color: '#dc2626', lineHeight: '1.5', marginTop: '2px' }}>{otaUrlError}</span>
                                            ) : (
                                                <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                    * Use your VPS domain or local network IP — <strong>not</strong> <code>localhost</code>
                                                </span>
                                            )}
                                        </div>

                                        {urlSuccess && (
                                            <div style={{
                                                background: '#f0fdf4',
                                                border: '1px solid #86efac',
                                                borderRadius: '10px',
                                                padding: '12px 18px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginTop: '4px'
                                            }}>
                                                <CheckCircleOutlined style={{ color: '#16a34a', fontSize: '16px' }} />
                                                <div style={{ color: '#15803d', fontWeight: 600, fontSize: '13px' }}>
                                                    Server URL saved to device memory successfully!
                                                </div>
                                            </div>
                                        )}

                                        <div style={{ marginTop: '8px' }}>
                                            <Button
                                                $primary
                                                $blackOnLight
                                                $size="14px"
                                                onClick={handleSaveServerUrl}
                                                disabled={urlLoading || !!otaUrlError}
                                                $padding="10px 20px"
                                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                            >
                                                {urlLoading ? <LoadingOutlined /> : <SyncOutlined />}
                                                Save Server URL
                                            </Button>
                                        </div>
                                    </Container>
                                </ContentCardRow>

                                <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
                                    <Container $display="flex" $flexDirection="column" $gap="8px">
                                        <DetailLabel style={{ fontWeight: 600 }}>Remote Biometric Deletion</DetailLabel>
                                        <MutedText>Instruct the local device database to erase a fingerprint template slot. This is irreversible.</MutedText>
                                        <Container $display="flex" $gap="12px" $alignItems="center" $flexWrap="wrap" $marginTop="4px">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Slot ID:</span>
                                                <input
                                                    type="number"
                                                    min={1}
                                                    max={162}
                                                    value={deleteSlotId}
                                                    onChange={(e) => setDeleteSlotId(parseInt(e.target.value) || '')}
                                                    style={{
                                                        width: '80px',
                                                        height: '38px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #d9d9d9',
                                                        padding: '0 10px',
                                                        outline: 'none',
                                                        fontSize: '14px'
                                                    }}
                                                />
                                            </div>
                                            <Button
                                                type="button"
                                                $primary={false}
                                                $size="14px"
                                                danger
                                                onClick={handleDeleteSlot}
                                                disabled={deleteLoading}
                                                $padding="10px 16px"
                                                style={{
                                                    backgroundColor: '#ef4444',
                                                    color: 'white',
                                                    border: 'none',
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                {deleteLoading ? <LoadingOutlined /> : <DeleteOutlined />}
                                                Delete Slot Template
                                            </Button>
                                        </Container>
                                    </Container>
                                </ContentCardRow>
                            </>
                        ) : (
                            <>
                                {!serialSupported ? (
                                    <ContentCardRow style={{ background: '#fff1f2', borderLeft: '4px solid #f43f5e', padding: '14px 20px', flexDirection: 'column', alignItems: 'stretch' }}>
                                        <div style={{ color: '#be123c', fontWeight: 600, fontSize: '13px' }}>Web Serial API Not Supported:</div>
                                        <div style={{ fontSize: '12.5px', color: '#e11d48', marginTop: '4px', lineHeight: '1.5' }}>
                                            Your current web browser does not support the Web Serial interface. 
                                            Please use a modern Chromium-based browser (Google Chrome, Microsoft Edge, or Opera) to connect and configure the physical device directly over a USB cable.
                                        </div>
                                    </ContentCardRow>
                                ) : (
                                    <>
                                        <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch', gap: '16px' }}>
                                            <Container $display="flex" $flexDirection="column" $gap="8px">
                                                <DetailLabel style={{ fontWeight: 600 }}>ESP32 USB Direct Provisioning</DetailLabel>
                                                <MutedText>
                                                    Plug the ESP32 board into a USB port on your computer and establish a direct connection to write credentials.
                                                </MutedText>
                                                <Container $marginTop="6px">
                                                    {!serialConnected ? (
                                                        <Button
                                                            $primary
                                                            $blackOnLight
                                                            $size="14px"
                                                            onClick={connectSerial}
                                                            $padding="10px 18px"
                                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                                        >
                                                            <UsbOutlined />
                                                            Connect ESP32 via USB
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            $primary={false}
                                                            $size="14px"
                                                            onClick={disconnectSerial}
                                                            $padding="10px 18px"
                                                            style={{
                                                                backgroundColor: '#ef4444',
                                                                color: 'white',
                                                                border: 'none',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <CloseCircleOutlined />
                                                            Disconnect Device USB
                                                        </Button>
                                                    )}
                                                </Container>
                                            </Container>
                                        </ContentCardRow>

                                        {serialConnected && (
                                            <>
                                                {/* Provisioning Form */}
                                                <ContentCardRow style={{ background: '#f9fafb', flexDirection: 'column', alignItems: 'stretch', gap: '14px', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
                                                    <div style={{ fontWeight: 600, fontSize: '13.5px', color: '#374151' }}>Device Network & Host Configuration:</div>
                                                    
                                                    {/* WiFi Setup Block */}
                                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '12px', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
                                                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <WifiOutlined style={{ color: '#4f46e5' }} /> 1. Device WiFi Credentials
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>WiFi SSID (SSID Name)</label>
                                                                <Input 
                                                                    placeholder="e.g. Office_WiFi"
                                                                    value={usbSsid}
                                                                    onChange={(e) => setUsbSsid(e.target.value)}
                                                                />
                                                            </div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>WiFi Password</label>
                                                                <Input
                                                                    isPassword
                                                                    placeholder="WiFi Password"
                                                                    value={usbPass}
                                                                    onChange={(e) => setUsbPass(e.target.value)}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: '4px' }}>
                                                            <Button
                                                                $primary
                                                                $blackOnLight
                                                                $size="13px"
                                                                onClick={handleSendWifiConfig}
                                                                disabled={sendingWifi}
                                                                $padding="8px 16px"
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                                            >
                                                                {sendingWifi ? <LoadingOutlined /> : <SyncOutlined />}
                                                                Save WiFi Settings (USB)
                                                            </Button>
                                                        </div>
                                                    </div>

                                                    {/* Server Host Setup Block */}
                                                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', background: '#ffffff', display: 'flex', flexDirection: 'column', gap: '12px', boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)' }}>
                                                        <div style={{ fontWeight: 600, fontSize: '13px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                            <SyncOutlined style={{ color: '#4f46e5' }} /> 2. NestJS Backend Host Server
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#4b5563' }}>NestJS Backend URL (VPS/Local Domain)</label>
                                                            <Input 
                                                                placeholder="e.g. https://api.yourdomain.com or http://192.168.1.5:4009"
                                                                value={usbServerUrl}
                                                                onChange={(e) => setUsbServerUrl(e.target.value)}
                                                            />
                                                            <span style={{ fontSize: '11px', color: '#9ca3af' }}>
                                                                * CRITICAL: Do <strong>NOT</strong> use <code>localhost</code> or <code>127.0.0.1</code>! Use your computer's local network IP or your actual VPS production URL.
                                                            </span>
                                                        </div>
                                                        <div style={{ marginTop: '4px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                                            <Button
                                                                $primary
                                                                $blackOnLight
                                                                $size="13px"
                                                                onClick={handleSendServerConfig}
                                                                disabled={sendingServer}
                                                                $padding="8px 16px"
                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                                                            >
                                                                {sendingServer ? <LoadingOutlined /> : <SyncOutlined />}
                                                                Save Server URL (USB)
                                                            </Button>
                                                            <Button
                                                                $primary={false}
                                                                $size="13px"
                                                                onClick={handleTriggerPing}
                                                                $padding="8px 16px"
                                                                style={{
                                                                    border: '1px solid #4f46e5',
                                                                    color: '#4f46e5',
                                                                    background: 'transparent',
                                                                    fontWeight: 600,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '6px',
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <WifiOutlined />
                                                                Test Ping (USB)
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </ContentCardRow>

                                                {/* Live Serial terminal monitor screen */}
                                                <ContentCardRow style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                        <span style={{ fontWeight: 600, fontSize: '12.5px', color: '#4b5563' }}>Live Serial Terminal Monitor Output:</span>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280', cursor: 'pointer', userSelect: 'none' }}>
                                                                <input 
                                                                    type="checkbox" 
                                                                    checked={autoScroll} 
                                                                    onChange={(e) => setAutoScroll(e.target.checked)} 
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                                Auto-scroll
                                                            </label>
                                                            <span style={{ fontSize: '11px', color: '#10b981', background: '#ecfdf5', padding: '2px 8px', borderRadius: '12px', fontWeight: 600 }}>ONLINE</span>
                                                        </div>
                                                    </div>

                                                    <div style={{
                                                        background: '#121212',
                                                        color: '#33ff33',
                                                        fontFamily: 'Consolas, Monaco, monospace',
                                                        padding: '16px',
                                                        borderRadius: '8px',
                                                        height: '240px',
                                                        overflowY: 'auto',
                                                        fontSize: '12px',
                                                        boxShadow: 'inset 0 2px 8px rgba(0,0,0,0.8)',
                                                        border: '1px solid #2e2e2e',
                                                        lineHeight: '1.6'
                                                    }}>
                                                        {serialLogs.length === 0 ? (
                                                            <div style={{ color: '#777' }}>Listening for serial logs from ESP32... Swipe card or touch sensor.</div>
                                                        ) : (
                                                            serialLogs.map((log, index) => (
                                                                <div key={index} style={{ wordBreak: 'break-all' }}>
                                                                    {log.startsWith('[Sent]') ? (
                                                                        <span style={{ color: '#38bdf8' }}>{log}</span>
                                                                    ) : log.startsWith('[CMD Sent]') ? (
                                                                        <span style={{ color: '#f43f5e' }}>{log}</span>
                                                                    ) : (
                                                                        <span>{log}</span>
                                                                    )}
                                                                </div>
                                                            ))
                                                        )}
                                                        <div ref={logsEndRef} />
                                                    </div>

                                                    {/* Quick Terminal command Sender */}
                                                    <form onSubmit={handleSendCustomCommand} style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                                                        <Input
                                                            placeholder="Type custom command (e.g. STATUS, HELP, 2) and press Enter..."
                                                            value={customCommand}
                                                            onChange={(e) => setCustomCommand(e.target.value)}
                                                            style={{ flex: 1, fontFamily: 'monospace' }}
                                                        />
                                                        <Button
                                                            $primary
                                                            $blackOnLight
                                                            $size="14px"
                                                            type="submit"
                                                            $padding="8px 16px"
                                                        >
                                                            Send
                                                        </Button>
                                                    </form>
                                                </ContentCardRow>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </ContentCard>
                </>
            )}

            {showOrigins && (
                <>
                    {!isCompanyAdmin ? (
                        <ContentCard $marginBottom="0" $overflow="hidden">
                            <ContentCardHeader>
                                <ContentCardTitle>Source Origins</ContentCardTitle>
                            </ContentCardHeader>
                            <ContentCardRow>
                                <MutedText>Only Company Admin can manage allowed origins for lead capture.</MutedText>
                            </ContentCardRow>
                        </ContentCard>
                    ) : (
                        <ContentCard $marginBottom="0" $overflow="hidden">
                            <ContentCardHeader>
                                <ContentCardTitle>Allowed origins (lead capture)</ContentCardTitle>
                            </ContentCardHeader>
                            <ContentCardRow>
                                <Container $flex="1" $minWidth="0" $display="flex" $flexDirection="column" $gap="8px">
                                    <DetailLabel as="div">Origins</DetailLabel>
                                    <MutedText>
                                        Add allowed origin URLs for lead-capture API validation (e.g.
                                        https://app.example.com)
                                    </MutedText>
                                    <Container $display="flex" $gap="8px" $alignItems="center" $flexWrap="wrap">
                                        <Input
                                            placeholder="https://example.com"
                                            value={newOrigin}
                                            onChange={(e) => setNewOrigin(e.target.value)}
                                            onPressEnter={addOrigin}
                                            style={{ maxWidth: 320, flex: 1, minWidth: 200 }}
                                        />
                                        <Button
                                            $primary
                                            $blackOnLight
                                            $size="14px"
                                            icon={<PlusOutlined />}
                                            onClick={addOrigin}
                                            $padding="18px 16px"
                                        >
                                            Add
                                        </Button>
                                    </Container>
                                    {local.origins.length > 0 && (
                                        <Container $display="flex" $flexDirection="column" $gap="6px" $marginTop="8px">
                                            {local.origins.map((origin, index) => (
                                                <ContentCardRow
                                                    key={`${origin}-${index}`}
                                                    style={{ alignItems: 'center', justifyContent: 'space-between' }}
                                                >
                                                    <MutedText style={{ wordBreak: 'break-all' }}>{origin}</MutedText>
                                                    <Button
                                                        type="button"
                                                        $primary={false}
                                                        $size="14px"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => removeOrigin(index)}
                                                        $padding="4px 8px"
                                                    >
                                                        Remove
                                                    </Button>
                                                </ContentCardRow>
                                            ))}
                                        </Container>
                                    )}
                                </Container>
                            </ContentCardRow>
                            <ContentCardFooter>
                                <Button
                                    $primary
                                    $blackOnLight
                                    $size="14px"
                                    onClick={handleSaveOrigins}
                                    loading={patchOriginsMutation.isPending}
                                    disabled={!hasChangesOrigins}
                                >
                                    Save changes
                                </Button>
                            </ContentCardFooter>
                        </ContentCard>
                    )}
                </>
            )}
        </Container>
    );
};

export default Settings;
